import {view, tokens, Divider, css} from '@sail/ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {
  ThemeProvider,
  LinkTheme,
  LinkAvatar,
  getLinkAppBaseUrl,
} from '@stripe-internal/conversations-ui';
import {defineMessages, FormattedMessage} from 'react-intl';
import {safeWindowOpen} from '@stripe-internal/safe-links';

export type ValueProps = {
  account_email: string;
  account_avatar: {
    content: string;
    color: string;
  };
  account_name: string;
  active_subscriptions: string;
};

const messages = defineMessages({
  accountTitle: {
    id: 'mse_inline_data.link_account_details.account_title',
    description: 'Account label',
    defaultMessage: 'Account',
  },
  activeSubscriptionsTitle: {
    id: 'mse_inline_data.link_account_details.active_subscriptions_title',
    description: 'Active subscriptions label',
    defaultMessage: 'Active subscriptions',
  },
});

const LinkAccountDetailsCard = ({
  props,
}: {
  props: InlineDatumReactComponentProps;
}) => {
  const {datum} = props;
  const accountPropsValue = datum.value as ValueProps;
  const {
    account_email: accountEmail,
    account_name: accountName,
    account_avatar: accountAvatar,
    active_subscriptions: activeSubscriptions,
  } = accountPropsValue;

  const analytics = useAnalytics();
  analytics.viewed(`viewed_link_link_account_details`);

  const cardStyles = css({
    backgroundColor: tokens.color.background.surface,
    border: '1px solid',
    borderColor: tokens.color.border,
    borderRadius: '16px',
    padding: '16px',
    gap: '12px',
    width: 'fill',
    cursor: 'pointer',
    ':hover': {
      boxShadow:
        '0px 7px 14px rgba(23, 23, 23, 0.08), 0px 3px 6px rgba(23, 23, 23, 0.12)',
    },
  });
  const titleStyles = css({
    fontWeight: '500',
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '-0.006em',
  });
  const subTitleDataStyles = css({
    fontWeight: '400',
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '-0.006em',
  });

  const handleClick = () => {
    analytics.action(`clicked_link_account_details`);
    const linkAppBaseUrl = getLinkAppBaseUrl();
    safeWindowOpen(`${linkAppBaseUrl}`);
  };

  return (
    <view.div css={{gap: 'medium', stack: 'y'}}>
      <view.div
        uses={[cardStyles]}
        onClick={handleClick}
        data-testid="link-account-card"
      >
        <view.div css={{stack: 'y', gap: '12px'}}>
          <view.div css={{stack: 'x', gap: '6px'}}>
            <view.span css={{textColor: tokens.color.text.onAction.secondary}}>
              <FormattedMessage {...messages.accountTitle} />
            </view.span>
            <view.span css={{height: '10px', stack: 'x'}}>
              <Divider />
            </view.span>
            <view.span css={{textColor: tokens.color.text.subdued}}>
              {accountEmail}
            </view.span>
          </view.div>

          <view.div css={{stack: 'x', gap: '8px', alignY: 'center'}}>
            <LinkAvatar
              content={accountAvatar.content || accountName}
              color={accountAvatar.color}
              size="xs"
              data-testid="account-avatar"
            />

            <view.div
              css={{
                fontSize: '24px',
                lineHeight: '32px',
                fontWeight: '600',
                letterSpacing: '-0.02em',
                color: LinkTheme.primaryActionColor,
              }}
              data-testid="account-name"
            >
              {accountName}
            </view.div>
          </view.div>

          <view.div
            css={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '16px',
              color: LinkTheme.primaryActionColor,
            }}
          >
            <view.div css={{stack: 'y', gap: '2x'}}>
              <view.div uses={[titleStyles]}>
                <FormattedMessage {...messages.activeSubscriptionsTitle} />
              </view.div>
              <view.div
                uses={[subTitleDataStyles]}
                data-testid="active-subscriptions"
              >
                {activeSubscriptions}
              </view.div>
            </view.div>
          </view.div>
        </view.div>
      </view.div>
    </view.div>
  );
};

export const LinkAccountDetails = (props: InlineDatumReactComponentProps) => {
  return (
    <ThemeProvider value={LinkTheme}>
      <LinkAccountDetailsCard props={props} />
    </ThemeProvider>
  );
};
